import { Information } from '@components/Information';
import { Box, Stack, styled, Typography } from '@mui/material';
import { selectionsApiSlice } from '@src/api';
import { theme } from '@src/theme';
import { border, GUID_EMTY } from '@src/utils';
import { useFormContext } from 'react-hook-form';
import { SelectTests } from './SelectTests';
import { Tag } from '@components/Tag';
import { FC, memo, useEffect, useMemo, useState } from 'react';
import { usePrevious } from '@pages/Selections/hooks/usePrevious';
import { PreloaderBubbles } from '@components/PreloaderBubbles';
import { useLoadingContext } from '../ContentLoading';

const gridTemplateColumns = 'minmax(0, 1fr) 1fr';

const Row = styled(Stack)(() => ({
  display: 'grid',
  gridTemplateColumns,
  borderBottom: border,
  height: '36px',
  alignItems: 'center',
}));

export const Tests: FC<{ selectionId: number }> = memo(({ selectionId }) => {
  const { watch, setValue } = useFormContext();
  const { setLoadingState } = useLoadingContext() || {};

  const [laboratoryId, providerId, additionalProductIds] = watch([
    'laboratoryId',
    'providerId',
    'additionalProductIds',
  ]);
  const [showLoader, setShowLoader] = useState(false);

  const prevParams = usePrevious({ laboratoryId, providerId });

  const {
    data: missedOrderProducts,
    isFetching,
    isSuccess,
  } = selectionsApiSlice.useGetMissingOrderProductsQuery(
    {
      id: selectionId,
      data: {
        laboratoryId,
        providerMaintenanceId: providerId === GUID_EMTY ? null : providerId,
      },
    },
    {
      skip: !laboratoryId,
    },
  );

  useEffect(() => {
    if (
      (laboratoryId && prevParams?.laboratoryId !== laboratoryId) ||
      (providerId && prevParams?.providerId !== providerId)
    ) {
      setShowLoader(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laboratoryId, providerId]);

  useEffect(() => {
    if (!isFetching) {
      setShowLoader(false);
    }
  }, [isFetching]);

  useEffect(() => {
    if (isSuccess) setLoadingState && setLoadingState(prev => ({ ...prev, biomarkers: true }));
  }, [isSuccess, setLoadingState]);

  useEffect(() => {
    if (missedOrderProducts) {
      setValue(
        'productsByLoincItems',
        missedOrderProducts
          .filter(({ product }) => !!product?.id)
          .map(({ loincItem, product }) => ({ loincItemId: loincItem?.id, productId: product?.id })),
      );
    }
  }, [missedOrderProducts, setValue]);

  const onDeleteTest = (id: string) => {
    const newAdditioanlTests = additionalProductIds.filter(({ id: productId }) => id !== productId);
    setValue('additionalProductIds', newAdditioanlTests);
  };

  const informationText = useMemo(() => {
    if (!missedOrderProducts || (missedOrderProducts && missedOrderProducts.length === 0)) return null;
    if (missedOrderProducts?.every(({ product }) => !product || !product?.id))
      return 'The system could not identify suitable tests for the missed tests listed in the generated Selection file.';
    return 'This list shows missed tests from the selection file and associated tests automatically identified by the system. Draft orders will assign these tests to patients based on their missed tests.';
  }, [missedOrderProducts]);

  return (
    <Stack sx={{ backgroundColor: theme.palette.grey['200'] }} gap="12px" padding="12px 18px">
      <Typography color="grey.700" variant="body16Bold" lineHeight="24px">
        Tests
      </Typography>
      {informationText && <Information description={informationText} />}
      {showLoader ? (
        <Box position="relative" mt="18px" height="42px" width="56px">
          <PreloaderBubbles position="absolute" sx={{ left: 0, top: 0, bgcolor: 'transparent' }} />
        </Box>
      ) : (
        <>
          {missedOrderProducts && missedOrderProducts?.length > 0 && (
            <Stack>
              <Row>
                <Typography color="grey.700" variant="label12Bold" lineHeight="18px">
                  Missed biomarkers
                </Typography>
                <Typography color="grey.700" variant="label12Bold" lineHeight="18px">
                  Matched tests
                </Typography>
              </Row>
              {missedOrderProducts?.map(({ loincItem, product }) => (
                <Row key={loincItem?.id}>
                  <Typography color="grey.700" variant="label12Bold" lineHeight="18px" noWrap marginRight="24px">
                    {loincItem?.code}&nbsp;
                    <Typography
                      color="grey.700"
                      variant="label12Medium"
                      lineHeight="18px"
                      component="span"
                      sx={{
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginRight: '24px',
                      }}
                    >
                      {loincItem?.longCommonName}
                    </Typography>
                  </Typography>
                  <Typography color="grey.700" variant="label12Medium" lineHeight="18px">
                    {product?.name || '—'}
                  </Typography>
                </Row>
              ))}
            </Stack>
          )}
          <SelectTests />
        </>
      )}

      <Stack gap="12px">
        {additionalProductIds.map(product => (
          <Tag contentText={product.name} value={product.id} onDelete={onDeleteTest} />
        ))}
      </Stack>
    </Stack>
  );
});
